import type {
  Project,
  ProjectListItem,
  PaginatedResponse,
  CreateProjectResponse,
  ProjectFormat,
  ProjectStatus,
} from './types'
import { getDefaultApiBase } from '../utils/environment'

// Storage keys
const API_BASE_KEY = 'dreamwright_api_base'
const GOOGLE_API_KEY = 'dreamwright_google_api_key'
const VEO3_API_KEYS_KEY = 'dreamwright_veo3_api_keys'
const MUSICGPT_API_KEY_KEY = 'dreamwright_musicgpt_api_key'
const PROMO_CODE_KEY = 'dreamwright_promo_code'
const VIDEO_MODEL_KEY = 'dreamwright_video_model'
const FAL_API_KEY = 'dreamwright_fal_api_key'
const VIDEO_SIMULATOR_MODE_KEY = 'dreamwright_video_simulator_mode'

// Auth token getter - set by AuthContext to provide Supabase JWT
let authTokenGetter: (() => Promise<string | null>) | null = null
// Cached token for sync access (used for asset URLs in <img> tags)
let cachedAuthToken: string | null = null

// Publication token for unauthenticated access to published project assets.
// Set when viewing a published project page; used as fallback when asset URLs
// in the snapshot weren't rewritten to signed Supabase URLs.
let currentPublicationToken: string | null = null

export function setPublicationToken(token: string | null): void {
  currentPublicationToken = token
}

export function getPublicationToken(): string | null {
  return currentPublicationToken
}

export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  authTokenGetter = getter
  // Update cached token immediately
  getter().then(token => {
    cachedAuthToken = token
  })
}

export function setCachedAuthToken(token: string | null): void {
  cachedAuthToken = token
}

export async function getAuthToken(): Promise<string | null> {
  if (authTokenGetter) {
    const token = await authTokenGetter()
    cachedAuthToken = token // Keep cache in sync
    return token
  }
  return null
}

export async function authenticatedFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = await getAuthToken()
  const headers = new Headers(init?.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  return fetch(url, { ...init, headers })
}

function getCachedAuthToken(): string | null {
  return cachedAuthToken
}

function getApiBase(): string {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return getDefaultApiBase()
    }
    return localStorage.getItem(API_BASE_KEY) || getDefaultApiBase()
  }
  return getDefaultApiBase()
}

export function setApiBase(url: string): void {
  if (url) {
    localStorage.setItem(API_BASE_KEY, url)
  } else {
    localStorage.removeItem(API_BASE_KEY)
  }
}

export function getConfiguredApiBase(): string {
  return getApiBase()
}

/**
 * Build a project asset URL. If the path is already an absolute URL
 * (e.g. a signed Supabase URL from a published snapshot), returns it as-is.
 */
export function buildProjectAssetUrl(projectId: string, assetPath: string): string {
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  return `${getApiBase()}/projects/${projectId}/${assetPath}`
}

// Google API Key management (stored in browser)
export function getGoogleApiKey(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(GOOGLE_API_KEY)
  }
  return null
}

export function setGoogleApiKey(key: string): void {
  if (key) {
    localStorage.setItem(GOOGLE_API_KEY, key)
  } else {
    localStorage.removeItem(GOOGLE_API_KEY)
  }
}

export function hasGoogleApiKey(): boolean {
  return !!getGoogleApiKey()
}

// VEO3 API Keys management (for video generation)
export function getVeo3ApiKeys(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(VEO3_API_KEYS_KEY)
  }
  return null
}

export function setVeo3ApiKeys(keys: string): void {
  if (keys) {
    localStorage.setItem(VEO3_API_KEYS_KEY, keys)
  } else {
    localStorage.removeItem(VEO3_API_KEYS_KEY)
  }
}

export function hasVeo3ApiKeys(): boolean {
  return !!getVeo3ApiKeys()
}

// MusicGPT API Key management (for music generation)
export function getMusicGptApiKey(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(MUSICGPT_API_KEY_KEY)
  }
  return null
}

export function setMusicGptApiKey(key: string): void {
  if (key) {
    localStorage.setItem(MUSICGPT_API_KEY_KEY, key)
  } else {
    localStorage.removeItem(MUSICGPT_API_KEY_KEY)
  }
}

export function hasMusicGptApiKey(): boolean {
  return !!getMusicGptApiKey()
}

// Legacy Suno API compatibility functions (for backwards compatibility with SettingsPage)
export function getSunoApiKey(): string | null {
  return getMusicGptApiKey()
}

export function setSunoApiKey(key: string): void {
  setMusicGptApiKey(key)
}

export function hasSunoApiKey(): boolean {
  return hasMusicGptApiKey()
}

export function getSunoApiBase(): string | null {
  // Suno base URL is no longer used, return null
  return null
}

export function setSunoApiBase(base: string): void {
  // Suno base URL is no longer used, no-op
  void base
}

export function hasSunoApiBase(): boolean {
  // Suno base URL is no longer used
  return false
}

// Promo Code management
export function getPromoCode(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(PROMO_CODE_KEY)
  }
  return null
}

export function setPromoCode(code: string): void {
  if (code) {
    localStorage.setItem(PROMO_CODE_KEY, code)
  } else {
    localStorage.removeItem(PROMO_CODE_KEY)
  }
}

export function hasPromoCode(): boolean {
  return !!getPromoCode()
}

// Video Model management (veo or wan)
export type VideoModel = 'veo' | 'wan_flf2v' | 'kling'

export interface VideoModelCapabilities {
  model: VideoModel
  label: string
  durations_seconds: number[]
  resolutions: Array<'480p' | '720p' | '1080p'>
  supports_interpolation: boolean
  interpolation_requires_duration_seconds?: number | null
  requires_end_frame: boolean
}

export interface VideoModelsResponse {
  models: VideoModelCapabilities[]
}

export interface PricingItem {
  model_id: string
  model_provider: string
  operation_type: string
  sparks_per_unit: number
  unit_type: string
  display_name: string
}

export function getVideoModel(): VideoModel {
  if (typeof window !== 'undefined') {
    const model = localStorage.getItem(VIDEO_MODEL_KEY)
    // Back-compat: treat legacy "wan" as "wan_flf2v"
    return (model === 'wan_flf2v' || model === 'wan' ? 'wan_flf2v' : 'veo') as VideoModel
  }
  return 'veo'
}

export function setVideoModel(model: VideoModel): void {
  localStorage.setItem(VIDEO_MODEL_KEY, model)
}

// Fal API Key management (for Wan model)
export function getFalApiKey(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(FAL_API_KEY)
  }
  return null
}

export function setFalApiKey(key: string): void {
  if (key) {
    localStorage.setItem(FAL_API_KEY, key)
  } else {
    localStorage.removeItem(FAL_API_KEY)
  }
}

export function hasFalApiKey(): boolean {
  return !!getFalApiKey()
}

// Video Simulator Mode management (returns placeholder videos instead of calling real APIs)
export function getVideoSimulatorMode(): boolean {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(VIDEO_SIMULATOR_MODE_KEY) === 'true'
  }
  return false
}

export function setVideoSimulatorMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(VIDEO_SIMULATOR_MODE_KEY, 'true')
  } else {
    localStorage.removeItem(VIDEO_SIMULATOR_MODE_KEY)
  }
}


// Check if user has access to generation (either promo code or own API key)
export function hasGenerationAccess(): boolean {
  return hasPromoCode() || hasGoogleApiKey()
}

export function hasTrailerAccess(): boolean {
  return hasPromoCode() || (hasGoogleApiKey() && hasVeo3ApiKeys())
}

/**
 * Extract error message from API error response.
 * Handles different error formats:
 * 1. FastAPI's {detail: string}
 * 2. Our structured error {error: {message: string}}
 * 3. Dependency error {detail: {error: {...}, missing_dependencies: [...]}}
 */
function extractErrorMessage(errorData: unknown, fallback: string): string {
  if (!errorData || typeof errorData !== 'object') {
    return fallback
  }
  const data = errorData as Record<string, unknown>

  // Check for detail field
  if (typeof data.detail === 'string') {
    return data.detail
  }

  // Check for nested error in detail (dependency errors, etc.)
  if (data.detail && typeof data.detail === 'object') {
    const detail = data.detail as Record<string, unknown>
    // Dependency errors may come back as:
    // { detail: { detail: string, missing_dependencies: [...] } }
    if (typeof detail.detail === 'string') {
      const missingDeps = Array.isArray(detail.missing_dependencies) ? detail.missing_dependencies : null
      if (missingDeps && missingDeps.length > 0) {
        // Try to extract readable messages if present.
        const depMessages = missingDeps
          .map((dep) => {
            if (!dep || typeof dep !== 'object') return null
            const d = dep as Record<string, unknown>
            if (typeof d.message === 'string') return d.message
            if (typeof d.type === 'string') return d.type
            return null
          })
          .filter(Boolean)
          .slice(0, 5)
        if (depMessages.length > 0) {
          return `${detail.detail}\n\nMissing:\n- ${depMessages.join('\n- ')}`
        }
      }
      return detail.detail
    }
    if (detail.error && typeof detail.error === 'object') {
      const error = detail.error as Record<string, unknown>
      if (typeof error.message === 'string') {
        return error.message
      }
    }
  }

  // Check for top-level error object
  if (data.error && typeof data.error === 'object') {
    const error = data.error as Record<string, unknown>
    if (typeof error.message === 'string') {
      return error.message
    }
  }

  return fallback
}

class ApiClient {
  private async request<T>(path: string, options?: RequestInit & { includeApiKey?: boolean; includeTrailerKeys?: boolean }): Promise<T> {
    const baseUrl = getApiBase()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Include Supabase auth token if available
    const authToken = await getAuthToken()
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    // Include promo code if set (unlocks default API keys on backend)
    const promoCode = getPromoCode()
    if (promoCode) {
      headers['X-Promo-Code'] = promoCode
    }

    // Include Google API key for generation requests
    if (options?.includeApiKey) {
      const apiKey = getGoogleApiKey()
      if (apiKey) {
        headers['X-Google-API-Key'] = apiKey
      }
    }

    // Include trailer API keys for trailer generation
    if (options?.includeTrailerKeys) {
      const googleKey = getGoogleApiKey()
      const veo3Keys = getVeo3ApiKeys()
      const musicgptKey = getMusicGptApiKey()

      if (googleKey) headers['X-Google-API-Key'] = googleKey
      if (veo3Keys) headers['X-VEO3-API-Keys'] = veo3Keys
      if (musicgptKey) headers['X-MusicGPT-API-Key'] = musicgptKey
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(extractErrorMessage(errorData, response.statusText))
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    // Check content type to catch HTML responses (misconfigured API)
    const contentType = response.headers.get('content-type')
    if (contentType && !contentType.includes('application/json')) {
      const apiBase = getApiBase()
      if (!apiBase) {
        throw new Error(
          'API endpoint not configured. Click the Settings icon (gear) in the header to set your API URL.'
        )
      }
      throw new Error(
        `API returned unexpected content type: ${contentType}. Check your API endpoint configuration in Settings.`
      )
    }

    return response.json()
  }

  // API Keys (per-user, server-managed)
  async createApiKey(name: string): Promise<{ id: string; key: string; key_prefix: string; name: string }> {
    return this.request('/auth/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  async listApiKeys(): Promise<Array<{ id: string; key_prefix: string; name: string; created_at: string; last_used_at: string | null }>> {
    return this.request('/auth/api-keys')
  }

  async revokeApiKey(keyId: string): Promise<void> {
    return this.request(`/auth/api-keys/${keyId}`, { method: 'DELETE' })
  }

  // Projects
  async listProjects(limit = 50, offset = 0): Promise<PaginatedResponse<ProjectListItem>> {
    return this.request(`/projects?limit=${limit}&offset=${offset}`)
  }

  async getProject(projectId: string): Promise<Project> {
    return this.request(`/projects/${projectId}/full`)
  }

  async createProject(
    prompt: string,
    format: ProjectFormat = 'webtoon',
    name?: string
  ): Promise<CreateProjectResponse> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify({ prompt, format, name }),
    })
  }

  async deleteProject(projectId: string): Promise<void> {
    const baseUrl = getApiBase()
    const headers: Record<string, string> = {}

    // Include Supabase auth token if available
    const authToken = await getAuthToken()
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${baseUrl}/projects/${projectId}`, {
      method: 'DELETE',
      headers,
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(extractErrorMessage(errorData, 'Failed to delete project'))
    }
  }

  async cloneProject(projectId: string, name?: string): Promise<{ id: string; name: string; message: string }> {
    return this.request(`/projects/${projectId}/clone`, {
      method: 'POST',
      body: JSON.stringify(name ? { name } : {}),
    })
  }

  async updateProject(
    projectId: string,
    data: { name?: string; status?: ProjectStatus; has_celebrity?: boolean }
  ): Promise<ProjectListItem> {
    return this.request(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Assets
  getAssetUrl(projectId: string, assetPath: string, options?: { width?: number; bustCache?: string }): string {
    // Full URLs (e.g. Supabase public URLs from upload nodes) are used as-is
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
      return assetPath
    }

    const baseUrl = getApiBase()
    // Asset paths may already include 'assets/' prefix
    let url: string
    if (assetPath.startsWith('assets/')) {
      url = `${baseUrl}/projects/${projectId}/${assetPath}`
    } else {
      url = `${baseUrl}/projects/${projectId}/assets/${assetPath}`
    }
    // Build query params
    const params = new URLSearchParams()
    if (options?.width) {
      params.set('w', String(options.width))
    }
    if (options?.bustCache) {
      params.set('v', options.bustCache)
    }
    // Include auth token for <img> tags (can't use Authorization header)
    const token = getCachedAuthToken()
    if (token) {
      params.set('token', token)
    }
    // Include publication token for unauthenticated access to published project assets
    if (currentPublicationToken) {
      params.set('publication_token', currentPublicationToken)
    }
    const queryString = params.toString()
    if (queryString) {
      url += `?${queryString}`
    }
    return url
  }

  // Convenience method for thumbnails
  getThumbnailUrl(projectId: string, assetPath: string, width: number = 200, bustCache?: string): string {
    return this.getAssetUrl(projectId, assetPath, { width, bustCache })
  }

  // Get a library asset URL for thumbnails of library-sourced nodes
  getLibraryAssetUrl(assetPath: string): string {
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
      return assetPath
    }
    const baseUrl = getApiBase()
    const clean = assetPath.startsWith('assets/') ? assetPath.slice(7) : assetPath
    let url = `${baseUrl}/library/assets/${clean}`
    const token = getCachedAuthToken()
    if (token) {
      url += `?token=${encodeURIComponent(token)}`
    }
    return url
  }

  // Upload an asset file (image, video, audio) through the backend API
  async uploadAsset(
    projectId: string,
    file: File,
    name?: string
  ): Promise<{ path: string; filename: string; size: number }> {
    const baseUrl = getApiBase()
    const formData = new FormData()
    formData.append('file', file)
    if (name) formData.append('name', name)

    const headers: Record<string, string> = {}

    // Include Supabase auth token if available
    const authToken = await getAuthToken()
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(
      `${baseUrl}/projects/${projectId}/assets/upload`,
      {
        method: 'POST',
        body: formData,
        headers,
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(extractErrorMessage(errorData, 'Failed to upload file'))
    }

    return response.json()
  }

  // Sparks (credits)
  async getSparks(): Promise<{ balance: number }> {
    return this.request('/sparks')
  }

  async addSparks(sparks: number, idempotencyKey: string): Promise<{
    id: string
    type: string
    sparks: number
    balance_after: number
    idempotency_key: string
    description: string | null
    already_processed: boolean
    created_at: string
  }> {
    return this.request('/sparks/transactions', {
      method: 'POST',
      body: JSON.stringify({
        type: 'refund',
        idempotency_key: idempotencyKey,
        sparks: sparks,
        reason: 'Manual top-up',
      }),
    })
  }

  async getPricing(): Promise<{ pricing: PricingItem[] }> {
    return this.request('/pricing')
  }

  // Debug Feedback API
  async submitWorkspaceFeedback(
    projectId: string,
    request: DebugFeedbackRequest
  ): Promise<DebugFeedbackResponse> {
    return this.request(`/projects/${projectId}/workspace/feedback`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  /**
   * Cancel the running agent execution for a project.
   * RFC 0061: Stop button for canvas execution.
   */
  async cancelAgentExecution(projectId: string): Promise<{ cancelled: boolean; reset_nodes?: number; reason?: string }> {
    return this.request(`/projects/${projectId}/agentic/cancel-node`, {
      method: 'POST',
    })
  }

  async getCostSummary(projectId: string): Promise<CostSummaryResponse> {
    return this.request(`/projects/${projectId}/agentic/cost-summary`)
  }
}

export interface NodeUsageItem {
  node_id: string
  node_type: string
  input_tokens: number
  output_tokens: number
  llm_model: string
  token_cost_usd: number
  images_generated: number
  videos_generated: number
  video_seconds: number
  estimated_sparks: number
  charged_sparks: number
  duration_ms: number | null
  status: 'COMPLETED' | 'FAILED'
}

export interface CostSummaryResponse {
  total_sparks_charged: number
  total_sparks_estimated: number
  total_input_tokens: number
  total_output_tokens: number
  total_token_cost_usd: number
  total_images: number
  total_videos: number
  node_count: number
  nodes: NodeUsageItem[]
}

// Debug Feedback API types
export interface DebugFeedbackRequest {
  feedback: string
  category?: 'bug' | 'feature' | 'question' | 'other'
  browser_info?: {
    user_agent?: string
    url?: string
  }
}

export interface DebugFeedbackResponse {
  id: string
  timestamp: string
}

export const api = new ApiClient()

// Workspace Chat API (SSE streaming)
export interface WorkspaceChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface WorkspaceChatOptions {
  projectId: string
  workspaceId: string
  canvasId?: string  // Which canvas to operate on (default: 'project')
  messages: WorkspaceChatMessage[]
  onChunk: (text: string) => void
  onComplete: () => void
  onError: (error: Error) => void
}

/**
 * Send a chat message and stream the response via SSE.
 * Returns an AbortController to cancel the stream.
 */
export async function sendWorkspaceChat(options: WorkspaceChatOptions): Promise<AbortController> {
  const { projectId, workspaceId, canvasId = 'project', messages, onChunk, onComplete, onError } = options
  const controller = new AbortController()
  
  const baseUrl = getApiBase()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  
  // Include auth token if available
  const authToken = await (async () => {
    if (authTokenGetter) {
      return authTokenGetter()
    }
    return null
  })()
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }
  
  // Include promo code
  const promoCode = getPromoCode()
  if (promoCode) {
    headers['X-Promo-Code'] = promoCode
  }
  
  // Include Google API key
  const apiKey = getGoogleApiKey()
  if (apiKey) {
    headers['X-Google-API-Key'] = apiKey
  }
  
  try {
    const response = await fetch(
      `${baseUrl}/projects/${projectId}/workspaces/${workspaceId}/messages`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages, canvas_id: canvasId, stream: true }),
        signal: controller.signal,
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      // Handle different error formats from FastAPI
      let errorMessage = response.statusText
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail
        } else if (Array.isArray(errorData.detail)) {
          // FastAPI validation errors come as array of {msg, loc, type}
          errorMessage = errorData.detail
            .map((e: { msg?: string; message?: string }) => e.msg || e.message || JSON.stringify(e))
            .join('; ')
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail)
        }
      }
      throw new Error(errorMessage)
    }
    
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }
    
    const decoder = new TextDecoder()
    let buffer = ''
    
    const processStream = async () => {
      try {
        // Helper to process buffered lines
        const processBuffer = () => {
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') {
                onComplete()
                return true // Signal completion
              }
              if (data.startsWith('[ERROR]')) {
                onError(new Error(data.slice(8)))
                return true // Signal completion (with error)
              }
              onChunk(data)
            }
          }
          return false // Not yet complete
        }

        let done = false
        while (!done) {
          const result = await reader.read()
          done = result.done
          
          if (result.value) {
            buffer += decoder.decode(result.value, { stream: true })
            if (processBuffer()) return
          }
          
          if (done) {
            // Flush any remaining data in decoder
            buffer += decoder.decode()
            // Process any remaining buffer content
            if (buffer.trim()) {
              // Add newline to ensure the last line is processed
              buffer += '\n'
              if (processBuffer()) return
            }
          }
        }
        onComplete()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Stream was cancelled
          return
        }
        onError(err instanceof Error ? err : new Error(String(err)))
      }
    }
    
    processStream()
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)))
  }
  
  return controller
}

/**
 * Options for executing a workspace tool directly (without LLM).
 * Used by Continue buttons for deterministic workflow steps.
 */
export interface ExecuteToolOptions {
  projectId: string
  workspaceId: string
  canvasId?: string
  toolName: string
  toolArgs?: Record<string, unknown>
  onChunk: (chunk: string) => void
  onComplete: () => void
  onError: (error: Error) => void
}

/**
 * Execute a workspace tool directly without LLM involvement.
 * This is the unified path for Continue button workflow steps.
 * Returns an AbortController to cancel the stream.
 */
export async function executeWorkspaceTool(options: ExecuteToolOptions): Promise<AbortController> {
  const { projectId, workspaceId, canvasId = 'project', toolName, toolArgs = {}, onChunk, onComplete, onError } = options
  const controller = new AbortController()
  
  const baseUrl = getApiBase()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  
  // Include auth token if available
  const authToken = await (async () => {
    if (authTokenGetter) {
      return authTokenGetter()
    }
    return null
  })()
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }
  
  // Include promo code
  const promoCode = getPromoCode()
  if (promoCode) {
    headers['X-Promo-Code'] = promoCode
  }
  
  // Include Google API key
  const apiKey = getGoogleApiKey()
  if (apiKey) {
    headers['X-Google-API-Key'] = apiKey
  }
  
  try {
    const response = await fetch(
      `${baseUrl}/projects/${projectId}/workspaces/${workspaceId}/execute-tool`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ tool_name: toolName, tool_args: toolArgs, canvas_id: canvasId }),
        signal: controller.signal,
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      let errorMessage = response.statusText
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail
            .map((e: { msg?: string; message?: string }) => e.msg || e.message || JSON.stringify(e))
            .join('; ')
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail)
        }
      }
      throw new Error(errorMessage)
    }
    
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }
    
    const decoder = new TextDecoder()
    let buffer = ''
    
    const processStream = async () => {
      try {
        const processBuffer = () => {
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') {
                onComplete()
                return true
              }
              if (data.startsWith('[ERROR]')) {
                onError(new Error(data.slice(8)))
                return true
              }
              onChunk(data)
            }
          }
          return false
        }

        let done = false
        while (!done) {
          const result = await reader.read()
          done = result.done
          
          if (result.value) {
            buffer += decoder.decode(result.value, { stream: true })
            if (processBuffer()) return
          }
          
          if (done) {
            buffer += decoder.decode()
            if (buffer.trim()) {
              buffer += '\n'
              if (processBuffer()) return
            }
          }
        }
        onComplete()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        onError(err instanceof Error ? err : new Error(String(err)))
      }
    }
    
    processStream()
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)))
  }
  
  return controller
}

// ============================================================================
// Conversation API
// ============================================================================

export interface WorkflowState {
  stage: string  // 'idle', 'expand_story', 'generate_characters', etc.
  active_job_id?: string | null  // Job currently running
  pending_action?: string | null  // 'continue' | 'refine'
}

export interface Conversation {
  id: string
  project_id: string
  title: string
  active_canvas_id: string  // Which canvas this conversation is working on
  workflow?: WorkflowState | null  // Workflow progress tracking
  created_at: string
  updated_at: string
}

export interface ConversationWithMessages extends Conversation {
  messages: WorkspaceChatMessage[]
  // Note: canvas_state is no longer part of conversation - canvases are independent
}

export interface CanvasState {
  nodes: unknown[]
  edges: unknown[]
  viewport?: { x: number; y: number; zoom: number } | null
  tainted_nodes?: string[]
  updated_at?: string
}

// Helper for conversation API requests
async function conversationRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const baseUrl = getApiBase()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const authToken = await getAuthToken()
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  const promoCode = getPromoCode()
  if (promoCode) {
    headers['X-Promo-Code'] = promoCode
  }

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(extractErrorMessage(errorData, response.statusText))
  }

  // Handle empty responses (e.g., DELETE)
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text)
}

/**
 * List all conversations for a project
 */
export async function listConversations(projectId: string): Promise<Conversation[]> {
  const response = await conversationRequest<{ conversations: Conversation[] }>(
    `/projects/${projectId}/workspaces`
  )
  return response.conversations || []
}

/**
 * Create a new conversation
 */
export async function createConversation(
  projectId: string,
  options?: { title?: string; canvas_id?: string }
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  })
}

/**
 * Get or create the agent conversation (RFC 0036)
 */
export async function getOrCreateAgentConversation(
  projectId: string
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces/agent`, {
    method: 'POST',
  })
}

/**
 * Update a conversation (e.g., rename or switch canvas)
 */
export async function updateConversation(
  projectId: string,
  conversationId: string,
  options: { title?: string; canvas_id?: string; workflow?: WorkflowState }
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify(options),
  })
}

/**
 * Get a conversation with messages
 */
export async function getConversation(
  projectId: string,
  conversationId: string
): Promise<ConversationWithMessages> {
  return conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`)
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  projectId: string,
  conversationId: string
): Promise<void> {
  await conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`, {
    method: 'DELETE',
  })
}

/**
 * List all canvases for a project
 */
export async function listCanvases(
  projectId: string
): Promise<{ canvases: Array<{ id: string; updated_at: string; node_count: number }> }> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases`)
}

/**
 * Get canvas state by ID (canvases are independent from conversations)
 */
export async function getCanvasState(
  projectId: string,
  canvasId: string = 'project'
): Promise<CanvasState> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`)
}

/**
 * Save canvas state by ID
 */
export async function saveCanvasState(
  projectId: string,
  canvasId: string,
  canvas: CanvasState
): Promise<CanvasState> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`, {
    method: 'PUT',
    body: JSON.stringify(canvas),
  })
}

/**
 * Delete a canvas (cannot delete 'project' canvas)
 */
export async function deleteCanvas(
  projectId: string,
  canvasId: string
): Promise<void> {
  await conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`, {
    method: 'DELETE',
  })
}

/**
 * Canvas view option from the translator registry
 */
export interface CanvasOption {
  id: string
  display_name: string
  description: string
}

/**
 * Get available canvas view options (translators) for a project
 */
export async function getCanvasOptions(projectId: string): Promise<CanvasOption[]> {
  return conversationRequest(`/projects/${projectId}/canvas/options`)
}



// =============================================================================
// DAG Proposal API (RFC 0079: Manual Canvas Node Persistence)
// =============================================================================

/**
 * Node operation for DAG proposal
 */
export interface DAGNodeOp {
  op: 'add' | 'remove' | 'update'
  id: string
  type?: string  // Required for 'add'
  config?: Record<string, unknown>  // Optional config for 'add' or 'update'
}

/**
 * Edge operation for DAG proposal
 */
export interface DAGEdgeOp {
  op: 'add' | 'remove'
  from: string  // Source node ID (or "nodeId.portId" via encodeNodePort)
  to: string    // Target node ID (or "nodeId.portId" via encodeNodePort)
  from_port?: string  // Source output port (e.g. "out_character_sheet")
  to_port?: string    // Target input port (e.g. "in_reference_images")
}

/**
 * DAG proposal request
 */
export interface DAGProposalRequest {
  intent: string
  changes: {
    nodes?: DAGNodeOp[]
    edges?: DAGEdgeOp[]
  }
  manual?: boolean  // If true, marks workflow as tainted (user-initiated change)
}

/**
 * DAG proposal response
 */
export interface DAGProposalResponse {
  status: 'success' | 'error'
  applied_node_ids: string[]
  applied_edge_count: number
  error?: string
}

/**
 * Sync graph changes to the backend via DAG Proposal API.
 * This is the base function for all graph mutations.
 *
 * RFC 0079: Used by frontend to persist manually added nodes/edges to graph.json
 */
export async function syncGraphChanges(
  projectId: string,
  proposal: DAGProposalRequest
): Promise<DAGProposalResponse> {
  const baseUrl = getApiBase()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const authToken = await getAuthToken()
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  const promoCode = getPromoCode()
  if (promoCode) {
    headers['X-Promo-Code'] = promoCode
  }

  const response = await fetch(
    `${baseUrl}/projects/${projectId}/agentic/dag-proposal`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(proposal),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(
      errorData?.detail || `Failed to sync graph changes: ${response.statusText}`
    )
  }

  return response.json()
}

/**
 * Add nodes to the backend graph.
 * Convenience wrapper around syncGraphChanges.
 *
 * @param projectId - Project ID
 * @param nodes - Array of nodes to add (id, type, config)
 * @param options - Optional settings (intent message, manual flag)
 */
export async function addNodesToGraph(
  projectId: string,
  nodes: Array<{ id: string; type: string; config?: Record<string, unknown> }>,
  options?: { intent?: string; manual?: boolean }
): Promise<DAGProposalResponse> {
  return syncGraphChanges(projectId, {
    intent: options?.intent || `Add ${nodes.length} node(s) via frontend`,
    changes: {
      nodes: nodes.map((n) => ({
        op: 'add' as const,
        id: n.id,
        type: n.type,
        config: n.config,
      })),
    },
    manual: options?.manual ?? true,  // Default to manual for frontend-initiated changes
  })
}

/**
 * Update a node's config in the backend graph.
 * Convenience wrapper around syncGraphChanges with op: "update".
 *
 * @deprecated RFC-0328: Node config writes go through YDoc only.
 * The frontend writes to YDoc via writeNodeData(); YDocReader snapshots
 * in_* fields before agent execution. This REST call is no longer needed
 * for normal config updates. Retained for pick operations and Director
 * proposals that use op: "update".
 *
 * @param projectId - Project ID
 * @param nodeId - Node ID to update
 * @param config - Partial config to merge into the node's meta (uses backend meta field names)
 */
export async function updateNodeConfig(
  projectId: string,
  nodeId: string,
  config: Record<string, unknown>
): Promise<DAGProposalResponse> {
  return syncGraphChanges(projectId, {
    intent: `Update config for ${nodeId}`,
    changes: {
      nodes: [{ op: 'update' as const, id: nodeId, config }],
    },
    manual: true,
  })
}

/**
 * Execute a node through the Orchestrator (RFC-0098).
 *
 * Fire-and-forget: progress and completion arrive via the existing
 * /agent/connect SSE stream (node status changes propagate through YDoc).
 *
 * Node data (in_/out_ fields) must be written to YDoc before calling.
 * This endpoint only accepts commands: which node, what action, how many times.
 *
 * @param projectId - Project ID
 * @param nodeId - Node ID to execute
 * @param action - Action to perform (default "generate")
 * @param count - Number of sequential generations to run (1-6, default 1)
 */
export async function executeNode(
  projectId: string,
  nodeId: string,
  action?: string,
  count?: number
): Promise<{ status: string; node_id: string }> {
  const baseUrl = getApiBase()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  const authToken = await getAuthToken()
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  const body: Record<string, unknown> = { node_id: nodeId }
  if (action && action !== 'generate') {
    body.action = action
  }
  if (count !== undefined && count > 1) {
    body.count = count
  }

  const response = await fetch(
    `${baseUrl}/projects/${projectId}/agentic/execute-node`,
    {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(
      errorData?.detail || `Failed to execute node: ${response.statusText}`
    )
  }

  return response.json()
}

/**
 * Remove nodes from the backend graph.
 * Convenience wrapper around syncGraphChanges.
 *
 * @param projectId - Project ID
 * @param nodeIds - Array of node IDs to remove
 * @param options - Optional settings (intent message, manual flag)
 */
export async function removeNodesFromGraph(
  projectId: string,
  nodeIds: string[],
  options?: { intent?: string; manual?: boolean }
): Promise<DAGProposalResponse> {
  return syncGraphChanges(projectId, {
    intent: options?.intent || `Remove ${nodeIds.length} node(s) via frontend`,
    changes: {
      nodes: nodeIds.map((id) => ({
        op: 'remove' as const,
        id,
      })),
    },
    manual: options?.manual ?? true,
  })
}

/**
 * Encode a node ID with optional port as "node_id.port_id" for the DAG proposal API.
 */
function encodeNodePort(nodeId: string, port?: string): string {
  return port ? `${nodeId}.${port}` : nodeId
}

/**
 * Add edges to the backend graph.
 * Convenience wrapper around syncGraphChanges.
 *
 * @param projectId - Project ID
 * @param edges - Array of edges to add (from, to, with optional port qualifiers)
 * @param options - Optional settings (intent message, manual flag)
 */
export async function addEdgesToGraph(
  projectId: string,
  edges: Array<{ from: string; to: string; fromPort?: string; toPort?: string }>,
  options?: { intent?: string; manual?: boolean }
): Promise<DAGProposalResponse> {
  return syncGraphChanges(projectId, {
    intent: options?.intent || `Add ${edges.length} edge(s) via frontend`,
    changes: {
      edges: edges.map((e) => ({
        op: 'add' as const,
        from: encodeNodePort(e.from, e.fromPort),
        to: encodeNodePort(e.to, e.toPort),
      })),
    },
    manual: options?.manual ?? true,
  })
}

/**
 * Remove edges from the backend graph.
 * Convenience wrapper around syncGraphChanges.
 *
 * @param projectId - Project ID
 * @param edges - Array of edges to remove (from, to, with optional port qualifiers)
 * @param options - Optional settings (intent message, manual flag)
 */
export async function removeEdgesFromGraph(
  projectId: string,
  edges: Array<{ from: string; to: string; fromPort?: string; toPort?: string }>,
  options?: { intent?: string; manual?: boolean }
): Promise<DAGProposalResponse> {
  return syncGraphChanges(projectId, {
    intent: options?.intent || `Remove ${edges.length} edge(s) via frontend`,
    changes: {
      edges: edges.map((e) => ({
        op: 'remove' as const,
        from: encodeNodePort(e.from, e.fromPort),
        to: encodeNodePort(e.to, e.toPort),
      })),
    },
    manual: options?.manual ?? true,
  })
}

/**
 * Reorder edges to a target port.
 * Sends removes + re-adds in a single DAG proposal so the YDoc receives
 * one atomic update instead of two (which causes flicker/revert).
 *
 * @param projectId - Project ID
 * @param edges - Edges in the desired new order
 */
export async function reorderEdges(
  projectId: string,
  edges: Array<{ from: string; to: string; fromPort?: string; toPort?: string }>,
): Promise<DAGProposalResponse> {
  const removes = edges.map((e) => ({
    op: 'remove' as const,
    from: encodeNodePort(e.from, e.fromPort),
    to: encodeNodePort(e.to, e.toPort),
  }))
  const adds = edges.map((e) => ({
    op: 'add' as const,
    from: encodeNodePort(e.from, e.fromPort),
    to: encodeNodePort(e.to, e.toPort),
  }))
  const payload = {
    intent: `Reorder ${edges.length} edge(s) via frontend`,
    changes: { edges: [...removes, ...adds] },
    manual: true,
  }
  const resp = await syncGraphChanges(projectId, payload)
  return resp
}

// ---------------------------------------------------------------------------
// Publication API (RFC-0295 — Snapshot-based sharing)
// ---------------------------------------------------------------------------

export interface PublishResponse {
  share_token: string
  share_url: string
  version_number: number
  created_at: string
}

export interface PublicationVersionInfo {
  version_number: number
  version_slug: string
  share_url: string
  created_at: string
}

export interface PublicationStatus {
  is_published: boolean
  share_token?: string
  share_url?: string
  version_number?: number
  updated_at?: string
  versions: PublicationVersionInfo[]
}

export interface PublishedChatMessage {
  role: 'user' | 'assistant'
  content: string
  agent?: string
  timestamp?: string
  referenced_node_ids?: string[]
  questions?: Array<Record<string, unknown>>
}

export interface PublishedSnapshot {
  project_name: string
  project_id: string
  snapshot: {
    nodes: Array<{ id: string; type: string; position: { x: number; y: number }; data: Record<string, unknown> }>
    edges: Array<{ id: string; source: string; target: string; type?: string; sourceHandle?: string; targetHandle?: string }>
    viewport?: { x: number; y: number; zoom: number }
  }
  messages: PublishedChatMessage[]
  published_at: string
}

export interface VersionListItem {
  version_number: number
  created_at: string
  project_name: string
}

export interface VersionDetail {
  project_name: string
  snapshot: PublishedSnapshot['snapshot']
  published_at: string
  version_number: number
}

export interface LiveSnapshot {
  project_name: string
  snapshot: PublishedSnapshot['snapshot']
  is_live: boolean
}

export interface VersionShareLink {
  share_token: string
  share_url: string
  version_number: number
}

export async function publishProject(projectId: string): Promise<PublishResponse> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication`, {
    method: 'POST',
  })
  if (!resp.ok) throw new Error(`Failed to publish project: ${resp.status}`)
  return resp.json()
}

export async function unpublishProject(projectId: string): Promise<void> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication`, {
    method: 'DELETE',
  })
  if (!resp.ok) throw new Error(`Failed to unpublish project: ${resp.status}`)
}

export async function getPublicationStatus(projectId: string): Promise<PublicationStatus> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication`)
  if (!resp.ok) throw new Error(`Failed to get publication status: ${resp.status}`)
  return resp.json()
}

export async function getPublishedSnapshot(projectToken: string, versionSlug: string): Promise<PublishedSnapshot> {
  // Public endpoint — no auth needed
  const resp = await fetch(`${getApiBase()}/publications/${projectToken}/${versionSlug}`)
  if (!resp.ok) throw new Error(`Failed to get published snapshot: ${resp.status}`)
  return resp.json()
}

export async function getPublicationVersions(projectId: string): Promise<VersionListItem[]> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication/versions`)
  if (!resp.ok) throw new Error(`Failed to get publication versions: ${resp.status}`)
  return resp.json()
}

export async function getPublicationVersion(projectId: string, versionNumber: number): Promise<VersionDetail> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication/versions/${versionNumber}`)
  if (!resp.ok) throw new Error(`Failed to get publication version: ${resp.status}`)
  return resp.json()
}

export async function getPublicationLive(projectId: string): Promise<LiveSnapshot> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication/live`)
  if (!resp.ok) throw new Error(`Failed to get live snapshot: ${resp.status}`)
  return resp.json()
}

export async function createVersionShareLink(projectId: string, versionNumber: number): Promise<VersionShareLink> {
  const resp = await authenticatedFetch(`${getApiBase()}/projects/${projectId}/publication/versions/${versionNumber}/share`, {
    method: 'POST',
  })
  if (!resp.ok) throw new Error(`Failed to create version share link: ${resp.status}`)
  return resp.json()
}

export async function clonePublishedProject(
  projectToken: string,
  versionSlug: string,
): Promise<{ id: string; name: string; message: string }> {
  const resp = await authenticatedFetch(
    `${getApiBase()}/publications/${projectToken}/${versionSlug}/clone`,
    { method: 'POST' },
  )
  if (!resp.ok) throw new Error(`Failed to clone project: ${resp.status}`)
  return resp.json()
}
